import { createSharedRoot } from '@bz/request';
import { useCurrency } from '@bz/common';
import { createEventListenerMap } from '@solid-primitives/event-listener';
import { createDerivedStaticStore } from '@solid-primitives/static-store';
import { useAction } from '@solidjs/router';
import Decimal from 'decimal.js-light';

import { isEmpty, autoToggleBetType, generateUniqueKey } from './util';
import {
  BetSlipType,
  BetStatus,
  Odds,
  OddsExtra,
  OddsSettings,
  SimpleOdds,
  Simplify,
  ComboType,
  SingleSubType,
  BetInput,
} from './types';
import { BetsOdds, PlaceBetsItem, PlaceParams, BetPaymentMethod } from './types/betslip';
import { getBetCount } from './betting-systems';

// 默认投注金额 / Default bet amount
export const DEFAULT_AMOUNT = Object.freeze({
  amount: 100,
});

// combo or system bets
export const getComboOrSystemBets = (oddsData: BetsOdds[]) => {
  return oddsData.filter((o) => o.type !== SingleSubType.CustomBet && o.type !== SingleSubType.Outright);
};

export type BetSlipAction =
  | { type: 'TOGGLE_ODD'; odds: SimpleOdds }
  | { type: 'UPDATE_ODD_AMOUNT'; code: string; amount: number }
  | { type: 'UPDATE_CUSTOM_ODD_AMOUNT'; code: string; amount: number }
  | { type: 'MARK_BET_CHANGED'; code: string }
  | { type: 'REMOVE_ODD'; uniqueKey: string }
  | { type: 'REMOVE_SUB_ODD'; code: string; matchId: string }
  | { type: 'CLEAR_ALL_BETS' }
  | { type: 'SET_BET_TYPE'; betType: BetSlipType }
  | { type: 'TOGGLE_EXPANDED'; explicit?: boolean }
  | { type: 'SET_FAST_BET'; fastBet: boolean }
  | { type: 'SET_FAST_BET_OPTIONS'; options: number[] }
  | { type: 'SET_ODDS_SETTINGS'; settings: OddsSettings }
  | { type: 'SET_COMBO_AMOUNT'; amount: number }
  | { type: 'FAST_BET'; amount: number; currency?: string }
  | { type: 'CHANGE_BET_STATUS'; status: BetStatus }
  | { type: 'UPDATE_MAX_STAKE'; code: string; maxBetStake: number }
  | { type: 'SET_BET_SYSTEM_COMBO_TYPE'; comboType: string }
  | { type: 'SET_QUICK_BET'; quickBet: boolean }
  | { type: 'SET_QUICK_BET_AMOUNT'; quickBetAmount: number }
  | { type: 'SET_BET_INPUT_LIMIT_TIP'; isLimit: boolean };

type State = {
  betType: BetSlipType;
  fastBet: boolean;
  odds: SimpleOdds[];
  oddsSettings: OddsSettings;
  fastBetOptions: number[];
  comboAmount: number;
  betStatus: BetStatus;
  comboType: ComboType;
  systemOffered: boolean;
  quickBet: boolean;
  quickBetAmount?: number;
  isBetInputLimitTip?: boolean;
};

// —— 纯 reducer —— //
export const dispatcher = (
  state: {
    betType: BetSlipType;
    fastBet: boolean;
    odds: SimpleOdds[];
    oddsSettings: OddsSettings;
    fastBetOptions: number[];
    comboAmount: number;
    betStatus: BetStatus;
    comboType: ComboType;
    systemOffered: boolean;
    quickBet: boolean;
    quickBetAmount?: number;
    isBetInputLimitTip?: boolean;
  },
  action: BetSlipAction,
  settingExpanded: (value?: boolean | 'toggle') => boolean
): {
  betType: BetSlipType;
  fastBet: boolean;
  odds: SimpleOdds[];
  oddsSettings: OddsSettings;
  fastBetOptions: number[];
  comboAmount: number;
  betStatus: BetStatus;
  comboType: ComboType;
  systemOffered: boolean;
  quickBet: boolean;
  quickBetAmount?: number;
  isBetInputLimitTip?: boolean;
} => {
  switch (action.type) {
    case 'TOGGLE_ODD': {
      const incoming = action.odds;
      let nextOdds = [...state.odds];

      const type = incoming.type;

      if (type === SingleSubType.CustomBet) {
        const isExist = nextOdds.some(
          (item) => item.type === SingleSubType.CustomBet && item.uniqueKey! === incoming.uniqueKey
        );
        if (!isExist) nextOdds.push({ ...incoming, marketStatus: 1 });
      } else if (type === SingleSubType.Outright) {
        const idx = nextOdds.findIndex((o) => o.type !== SingleSubType.CustomBet && o.code === incoming.code);
        if (idx === -1) {
          // 新增普通单
          nextOdds = [...nextOdds, { ...incoming, type: SingleSubType.Outright, marketStatus: 1 } as any];
        } else {
          // 移除普通单
          nextOdds = nextOdds
            .filter((o) => o.type !== SingleSubType.CustomBet)
            .slice(0, idx)
            .concat(nextOdds.slice(idx + 1));
        }
      } else {
        const idx = nextOdds.findIndex((o) => o.type !== SingleSubType.CustomBet && o.code === incoming.code);
        if (idx === -1) {
          // 新增普通单
          nextOdds = [...nextOdds, { ...incoming, type: 'SINGLE', marketStatus: 1 } as any];
        } else {
          // 移除普通单
          nextOdds = nextOdds.slice(0, idx).concat(nextOdds.slice(idx + 1));
        }
      }

      /** 排序逻辑：防止 custom 排到奇怪的位置 */
      nextOdds.sort((a: BetsOdds, b: BetsOdds) => {
        const aId = Number(a.matchId?.split(':')[2] ?? 0);
        const bId = Number(b.matchId?.split(':')[2] ?? 0);
        return aId - bId;
      });

      /* BetType */
      const nextBetType = autoToggleBetType(nextOdds, state.betType);

      // 新增时重置成功态
      const added = !state.odds.some((o) => o.code === incoming.code);
      const nextStatus = state.betStatus === BetStatus.SUCCESS && added ? BetStatus.NORMAL : state.betStatus;

      if (!settingExpanded()) settingExpanded(true);

      // 如果没有登陆，先存一下
      if (!account.isLogin) sessionStorage.setItem('oddsData', JSON.stringify(nextOdds));

      // 为每一个item添加一个uninkey
      const oddsWithKey = nextOdds.map((o) => (o.type !== SingleSubType.CustomBet ? { ...o, uniqueKey: o.code } : o));

      return {
        ...state,
        comboAmount: 0,
        odds: oddsWithKey,
        betType: nextBetType,
        betStatus: nextStatus,
      };
    }
    case 'UPDATE_ODD_AMOUNT': {
      const odds = state.odds.map((o) => (o.code === action.code ? { ...o, amount: action.amount } : o));
      return { ...state, odds };
    }
    // update custom bet sub item amount 待定方案
    case 'UPDATE_CUSTOM_ODD_AMOUNT': {
      const odds = state.odds.map((o) => (o.code === action.code ? { ...o, amount: action.amount } : o));
      return { ...state, odds };
    }
    // 更新最大的下注金额
    case 'UPDATE_MAX_STAKE': {
      return {
        ...state,
        odds: state.odds.map((o) => (o.code === action.code ? { ...o, maxBetStake: action.maxBetStake } : o)),
      };
    }
    case 'MARK_BET_CHANGED': {
      const odds = state.odds.map((o) => (o.code === action.code ? { ...o, betChanged: true } : o));
      return { ...state, odds };
    }
    case 'REMOVE_ODD': {
      const odds = state.odds.filter((o) => !(o.uniqueKey === action.uniqueKey));

      // betslip 的 tab 类型自动切换
      const betType = autoToggleBetType(odds, state.betType);

      return { ...state, odds, betType };
    }
    case 'REMOVE_SUB_ODD': {
      const odds = state.odds.map((o) => {
        if (o.type !== SingleSubType.CustomBet) return o;

        return {
          ...o,
          selections: (o.selections ?? []).filter((s) => s.code !== action.code),
        };
      });

      return { ...state, odds };
    }
    case 'CLEAR_ALL_BETS': {
      const betType =
        state.betType === BetSlipType.Combo || state.betType === BetSlipType.System
          ? BetSlipType.Single
          : state.betType;

      sessionStorage.removeItem('oddsData');
      return { ...state, odds: [], betType };
    }
    case 'SET_BET_TYPE':
      return { ...state, betType: action.betType };
    case 'SET_BET_SYSTEM_COMBO_TYPE':
      return { ...state, comboType: action.comboType as ComboType };
    case 'SET_FAST_BET':
      return { ...state, fastBet: action.fastBet };
    case 'SET_FAST_BET_OPTIONS':
      return { ...state, fastBetOptions: action.options };
    case 'SET_ODDS_SETTINGS':
      return { ...state, oddsSettings: action.settings };
    case 'SET_BET_INPUT_LIMIT_TIP':
      return { ...state, isBetInputLimitTip: action.isLimit };
    case 'SET_COMBO_AMOUNT':
      return { ...state, comboAmount: action.amount };
    case 'FAST_BET': {
      const odds = state.odds.map((o) => ({
        ...o,
        amount: action.amount,
      }));
      return { ...state, odds, comboAmount: action.amount };
    }
    case 'CHANGE_BET_STATUS':
      return { ...state, betStatus: action.status };
    case 'SET_QUICK_BET':
      return { ...state, quickBet: action.quickBet };
    case 'SET_QUICK_BET_AMOUNT':
      return { ...state, quickBetAmount: action.quickBetAmount };
    default:
      return state;
  }
};

// 映射cartItem数据
const mapCartItems = (data: BetsOdds[]) => {
  const mapSelection = (s: any, amount: number) => {
    return {
      active: s.active,
      marketStatus: s.status,
      amount,
      code: s.code,
      marketName: s.marketName,
      matchId: s.matchId,
      odds: s.newOdds,
      outcomeId: s.outcomeId,
      outcomeName: s.outcomeName,
      phase: s.phase,
      type: s.itemType,
      specifiers: s.specifiers,
      marketId: s.marketId,
      colName: s.colName,
    };
  };

  return data.map((item: any) => {
    const baseSelection = item.selections?.[0];
    const amount = item.stake;

    const base = {
      uniqueKey: baseSelection.code,
      active: baseSelection.active,
      marketStatus: baseSelection.status,
      amount,
      code: baseSelection.code,
      marketName: baseSelection.marketName,
      matchId: baseSelection.matchId,
      odds: baseSelection.newOdds,
      outcomeId: baseSelection.outcomeId,
      outcomeName: baseSelection.outcomeName,
      phase: baseSelection.phase,
      type: item.itemType,
      specifiers: baseSelection.specifiers,
      colName: baseSelection.colName,
      marketId: baseSelection.marketId,
    };

    if (item.itemType === SingleSubType.CustomBet) {
      return {
        ...base,
        type: SingleSubType.CustomBet,
        uniqueKey: generateUniqueKey(item.selections),
        selections: item.selections.map((s: PlaceBetsItem) => mapSelection(s, amount)),
      };
    }

    return base;
  });
};

// 购物车。下注。参数转换方法
export const transformBets = (
  latestOdds: BetsOdds[],
  comboAmount: number,
  comboType: ComboType,
  betType: BetSlipType
) => {
  const isSingle = betType === BetSlipType.Single;
  const isCombo = betType === BetSlipType.Combo;
  const isSystem = betType === BetSlipType.System;
  const normalToSelection = <T extends { matchId: string; code: string; odds: number }>(o: T) => ({
    matchId: o.matchId,
    code: o.code,
    odds: o.odds,
  });
  if (isSingle) {
    return latestOdds.map((o) => {
      const isCustom = o.type === SingleSubType.CustomBet;
      const selections = isCustom && o.selections ? o.selections.map(normalToSelection) : [normalToSelection(o)];

      return {
        itemType: isCustom ? 'CUSTOM' : 'SINGLE',
        comboType: null,
        odds: o.odds,
        stake: o.amount,
        selections,
      };
    });
  }

  // ---- 合并所有 selection -----
  const allSelections = getComboOrSystemBets(latestOdds).map((o) => normalToSelection(o));

  const totalOdds = () => getComboOrSystemBets(latestOdds).reduce((acc, odd) => acc.mul(odd.odds), new Decimal(1));

  const finalOdds = isCombo ? totalOdds().toNumber() : 2; //system 没有总赔率 因为api要 所以给个2

  return [
    {
      itemType: isCombo ? BetSlipType.Combo : BetSlipType.System,
      comboType: isSystem ? comboType : null,
      odds: finalOdds,
      stake: totalStake(latestOdds, comboAmount, comboType, betType),
      selections: allSelections,
    },
  ];
};

/**
 * BetSlip all share store
 */
export const useBetSlipStore = createSharedRoot(() => {
  const { latestCurrency } = useCurrency();
  const fastBetCache = new Map<string, number[]>();

  const [state, setState] = createSignal<State>(initialBetSlipState);
  const store = createDerivedStaticStore(state);

  // 获取远程数据
  const [remoteCart] = createResource(
    () => account.isLogin,
    async (isLogin) => {
      if (!isLogin) return null;
      return await get.getUserCarts();
    }
  );

  /**
   * 深度合并本地与远程投注项
   */
  const mergeBetItems = (localOdds: any[], serverItems: any[]) => {
    const serverOdds = mapCartItems(serverItems);

    // 1. 创建一个 Map 方便快速查找，以 code/uniqueKey 为键
    const mergedMap = new Map<string, any>();

    // 2. 先把服务器的数据塞进去（作为基准）
    serverOdds.forEach((item: Record<string, any>) => mergedMap.set(item.uniqueKey || item.code, item));

    // 3. 用本地数据进行覆盖或新增
    localOdds.forEach((localItem) => {
      const key = localItem.uniqueKey || localItem.code;
      if (mergedMap.has(key)) {
        const serverItem = mergedMap.get(key);
        mergedMap.set(key, {
          ...serverItem,
          amount: localItem.amount || serverItem.amount,
        });
      } else {
        mergedMap.set(key, localItem);
      }
    });

    return Array.from(mergedMap.values());
  };

  createEffect(
    on(
      () => [account.isLogin, remoteCart.loading, remoteCart.error, remoteCart()],
      async ([isLogin, loading, fetchError, raw]) => {
        if (!isLogin || loading) return;

        const localData = JSON.parse(sessionStorage.getItem('oddsData') || '[]');
        type RemoteCartPayload = { items?: unknown[]; betType?: string; cartNo?: string };
        const serverData: RemoteCartPayload =
          fetchError != null
            ? { items: [] }
            : raw != null && typeof raw === 'object'
              ? (raw as RemoteCartPayload)
              : { items: [] };

        const serverItems = Array.isArray(serverData.items) ? serverData.items : [];

        const finalOdds = mergeBetItems(localData, serverItems);

        if (finalOdds.length === 0) return;

        setState((prev) => ({
          ...prev,
          odds: finalOdds,
          betType: (serverData.betType || prev.betType) as BetSlipType,
        }));

        if (localData.length > 0) {
          await saveCartToServer();
          sessionStorage.removeItem('oddsData');
        }
      }
    )
  );

  // 获取custom bet的赔率及冲突项
  const sendCalculateFilter = useAction(post.cbCalculateFilter);

  // 购物车展开/收起
  const [expanded, setExpanded] = createSignal(false);
  function settingExpanded(value?: boolean | 'toggle') {
    if (arguments.length) {
      value === 'toggle' ? setExpanded((prev) => !prev) : setExpanded(value!);
    }
    return expanded();
  }

  const saveCart = useAction(post.saveCart);
  const clearCart = useAction(post.clearUserCarts);

  const saveCartToServer = (aboutToClose?: boolean) => {
    if (store.odds.length === 0) return clearCart();

    const transformItems = (storeOdds: any[]) => {
      return storeOdds.map((odd) => {
        const isCustom = odd.type === SingleSubType.CustomBet;
        const selections = isCustom
          ? odd.selections.map((s: any) => ({
              matchId: s.matchId,
              code: s.code,
              odds: s.odds,
            }))
          : [
              {
                matchId: odd.matchId,
                code: odd.code,
                odds: odd.odds,
              },
            ];

        return {
          itemType: odd.type,
          odds: odd.odds,
          stake: odd.amount,
          selections,
        };
      });
    };

    const calcTotalStake = (storeOdds: BetsOdds[]) => storeOdds.reduce((sum, odd) => sum + (odd.amount || 0), 0);

    const cartData: Parameters<typeof post.saveCart>[0] = {
      cartNo: (remoteCart() as any)?.cartNo || '',
      userId: account.userId,
      betType: store.betType,
      totalStake: calcTotalStake(store.odds),
      currency: latestCurrency(),
      ...(store.betType === BetSlipType.System && {
        comboType: store.comboType,
      }),
      acceptOddsChange: store.oddsSettings,
      items: transformItems(store.odds),
    };

    // 确认下是前端保存的问题 还是 后端问题??
    const hasData = transformItems(store.odds).some((o) => o.selections.some((s: any) => !s.odds || !s.code));

    if (transformItems(store.odds).length > 0 && !hasData) {
      if (aboutToClose) {
        const url = `${window.location.origin}/api/order/user/v2/cart`;
        const blob = new Blob([JSON.stringify(cartData)], {
          type: 'application/json',
        });
        // 双重保障
        navigator.sendBeacon(url, blob);
      }
      saveCart(cartData);
    }
  };

  const handleCustomBetSelect = () => {
    const customBets = store.odds.filter((o) => o.type === SingleSubType.CustomBet);

    if (!customBets.length) return Promise.resolve();

    const tasks = customBets.map((customBet) => {
      const selections = customBet.selections.map((s: any) => ({
        matchId: s.matchId,
        marketId: s.marketId!,
        outcomeId: s.outcomeId,
        specifiers: s.specifiers!,
        code: s.code,
      }));

      return sendCalculateFilter({ selections })
        .then((res) => ({
          status: 'fulfilled' as const,
          uniqueKey: customBet.uniqueKey,
          res,
        }))
        .catch((error) => ({
          status: 'rejected' as const,
          uniqueKey: customBet.uniqueKey,
          error,
        }));
    });

    // 并行处理
    Promise.allSettled(tasks)
      .then((results) => {
        setState((prev) => ({
          ...prev,
          odds: prev.odds.map((o) => {
            if (o.type !== SingleSubType.CustomBet) return o;
            const result = results
              .map((r) => (r.status === 'fulfilled' ? r.value : r.reason))
              .find((r) => r.uniqueKey === o.uniqueKey);

            const customOdds = (Math.trunc(result.res.totalOdds * 100) / 100).toFixed(2);
            return {
              ...o,
              odds: Number(customOdds) ?? o.odds,
            };
          }),
        }));
      })
      .catch(() => {});
  };

  // 依据币种获取下注推荐金额
  const getFastBetOptionsByCurrency = async (currency: string) => {
    const cached = fastBetCache.get(currency);
    if (cached) {
      setState((prev) => {
        if (prev.fastBetOptions === cached) return prev;
        return {
          ...prev,
          fastBetOptions: cached.slice(0, 4),
        };
      });
      return;
    }

    try {
      const res = await get.getTicketsRecommendationAmounts({ currency });
      const fastBetOptions = res?.recommendedAmounts ?? [];
      if (!fastBetOptions.length) return;

      fastBetCache.set(currency, fastBetOptions);

      setState((prev) => ({
        ...prev,
        fastBetOptions: fastBetOptions.slice(0, 4),
      }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        fastBetOptions: [],
      }));
      console.warn('getRecommendationAmounts failed', err);
    }
  };

  // 依据不同的地区是否显示system玩法
  const getSystemOffered = async () => {
    try {
      const res = await get.getOddsBzOffered();
      if (res?.systemOffered) {
        setState((prev) => ({
          ...prev,
          systemOffered: res.systemOffered === 1 ? true : false,
        }));
      }
    } catch (err) {
      console.warn('getSystemOffered failed', err);
    }
  };

  createEffect(() => {
    if (account.isLogin) {
      createEventListenerMap(window, {
        beforeunload: () => {
          saveCartToServer(true);
        },
        visibilitychange: () => {
          if (document.visibilityState === 'hidden') {
            saveCartToServer();
          }
        },
      });
    }
  });

  // 依据ip显示system玩法
  createEffect(() => {
    if (account.isLogin) {
      getSystemOffered();
    }
  });

  // 获取快速投注金额
  createEffect(() => {
    const currency = latestCurrency();
    untrack(() => getFastBetOptionsByCurrency(currency));
  });

  // 获取custom bet的odds
  const customBetTrigger = createMemo(() => {
    const keys = store.odds.filter((o) => o.type === SingleSubType.CustomBet).map((o) => o.uniqueKey);
    return keys.join(',');
  });

  createEffect(
    on(
      customBetTrigger,
      (ids) => {
        if (ids) {
          handleCustomBetSelect();
        }
      },
      { defer: true }
    )
  );

  // 购物车
  createEffect(
    on(
      () => remoteCart(),
      (currentCart) => {
        if (!currentCart || isEmpty(currentCart)) return;

        const hasCustomBet = (currentCart as any).items?.some((item: any) => item.itemType === SingleSubType.CustomBet);

        if (hasCustomBet) {
          queueMicrotask(() => {
            const customInStore = store.odds.some((o) => o.type === SingleSubType.CustomBet);
            if (customInStore) {
              handleCustomBetSelect();
            }
          });
        }
      },
      { defer: true }
    )
  );

  return [
    store,
    (action: BetSlipAction) => {
      setState((prev) => dispatcher(prev, action, settingExpanded));
    },
    remoteCart.loading,
    settingExpanded,
    {
      handleCustomBetSelect,
      saveCartToServer,
      getFastBetOptionsByCurrency,
      getSystemOffered,
    },
  ] as const;
});

// 初始状态
const initialBetSlipState = {
  expanded: false,
  betType: BetSlipType.Single,
  fastBet: false,
  odds: [],
  oddsSettings: OddsSettings.ACCEPT_ANY_ODDS_CHANGE,
  fastBetOptions: [],
  comboAmount: 0,
  betStatus: BetStatus.NORMAL,
  comboType: ComboType.TwoOfThree, //system 玩法
  betsMaxCount: 50, // 默认最大投注数量50
  systemOffered: true, // 不同地区是否显示system的玩法
  quickBet: false, //是否启用快速投注
  quickBetAmount: undefined, //快速投注金额
  isBetInputLimitTip: false, //是否超出金额限制（最大或者最小）
};

/**
 * 投注赔率管理 Hook / Hook for managing bet odds
 * 用于添加/移除投注项 / Used for adding/removing bet items
 */
export const usePlaceOdd = () => {
  const [store, dispatch] = useBetSlipStore();

  const isOddPlaced = (code: string, isCustom?: boolean) => {
    // 如果是custom bet 则判断子的selections数组中的每一个code
    if (isCustom) {
      return store.odds.some(
        (odd) => odd.type === SingleSubType.CustomBet && odd.selections.some((s) => s.code === code)
      );
    }

    return store.odds.some((odd) => odd.type !== SingleSubType.CustomBet && odd.code === code);
  };

  const toggleOdd = (odds: SimpleOdds) => dispatch({ type: 'TOGGLE_ODD', odds });

  const toggleOddWithAuth = (odds: Simplify<Odds & Partial<OddsExtra>>) => {
    //如果处于快速投注模式，先退出快速投注模式
    if (store.quickBet) {
      dispatch({ type: 'SET_QUICK_BET', quickBet: false });
    }
    toggleOdd({ ...odds, amount: 0 } as SimpleOdds);
  };

  return { toggleOdd: toggleOddWithAuth, isOddPlaced };
};

/**
 * 获取每种玩法的最大投注金额
 */
export const totalStake = (odds: BetsOdds[], comboAmount: number, comboType: ComboType, betType: BetSlipType) => {
  let totalStake = new Decimal(0);
  switch (betType) {
    case BetSlipType.Single:
      totalStake = new Decimal(odds.reduce((acc, o) => acc + o.amount!, 0));
      break;
    case BetSlipType.Combo:
      totalStake = new Decimal(comboAmount);
      break;
    case BetSlipType.System:
      totalStake = new Decimal(comboAmount.toString()).mul(getBetCount(comboType));
      break;
  }
  return Number(totalStake);
};

/**
 * 盘口最大下注金额
 */
export const useMaxStakeAction = () => {
  const [store] = useBetSlipStore();
  const { latestCurrency, isBetCreditOn, source, addVipBalance, isCash } = useCurrency();
  const getMaxStake = useAction(post.maxStake);

  // 返回一个可以被防抖调用的异步函数
  return async (betParams: BetInput) => {
    if (store.odds.length === 0) return;

    const { amount, matchId, code } = betParams;
    // 过滤当前盘口数据
    let currBets = [];
    if (store.betType === BetSlipType.Single) {
      // custom bet 需要matchid 后续会更改。。。
      if (matchId) {
        currBets = store.odds.filter((o) => o.matchId === matchId && o.code === code);
      } else {
        currBets = store.odds.filter((o) => o.code === code);
      }
    } else {
      currBets = store.odds;
    }

    let totalStake: Decimal;
    if (store.betType === BetSlipType.System) {
      totalStake = new Decimal(amount.toString()).mul(getBetCount(store.comboType));
    } else {
      totalStake = new Decimal(amount);
    }

    // 拦截一下
    if (new Decimal(addVipBalance()).lt(totalStake)) return;

    const params: { [K in keyof PlaceParams]: PlaceParams[K] } = {
      betType: store.betType,
      totalStake: totalStake.toNumber(),
      currency: latestCurrency(),
      acceptOddsChange: OddsSettings.ACCEPT_ANY_ODDS_CHANGE,
      bets: transformBets(currBets, Number(amount), store.comboType, store.betType),
      useCredit: isBetCreditOn() && isCash() ? 1 : 0,
      paymentMethod: source() as BetPaymentMethod,
    };

    try {
      return await getMaxStake(params as any);
    } catch (error) {
      console.warn('Max stake failed', error);
      throw error;
    }
  };
};
